import { useMemo } from 'react'
import { useGeoUnits, useGeoLiveMetrics } from '../../hooks/useGeoAnalytics'
import { featureCollectionBounds } from '../analyticsMap/choropleth'
import { officeUnitType, type GuidedListSpec } from '../../types/guidedList'
import type { FilterGroup } from '../../types/contactList'
import { buildDensityPoints, findDistrictFeature, toFilterGroupRequests } from './reviewSpec'
import { DensityHeatmap } from '../shared/DensityHeatmap'

interface Props { spec: GuidedListSpec; groups: FilterGroup[] }

export function GuidedReviewMap({ spec, groups }: Props) {
  const districtUnits = useGeoUnits(spec.state ? { type: officeUnitType(spec.office), state: spec.state } : null)
  const precinctUnits = useGeoUnits(spec.state ? { type: 'precinct', state: spec.state } : null)
  const live = useGeoLiveMetrics(
    spec.state ? { unitType: 'precinct', state: spec.state, filterGroups: toFilterGroupRequests(groups) } : null,
  )

  const points = useMemo(
    () => (precinctUnits.data && live.data ? buildDensityPoints(precinctUnits.data, live.data) : null),
    [precinctUnits.data, live.data],
  )
  const boundary = useMemo(() => {
    const f = districtUnits.data ? findDistrictFeature(districtUnits.data, spec.districtCode) : null
    return f ? ({ type: 'FeatureCollection', features: [f] } as GeoJSON.FeatureCollection) : null
  }, [districtUnits.data, spec.districtCode])
  const fitBounds = useMemo(() => (boundary ? featureCollectionBounds(boundary) : null), [boundary])

  return (
    <DensityHeatmap
      points={points}
      boundary={boundary}
      fitBounds={fitBounds}
      caption="Stylized target-density texture by precinct (k≥10 floored). Illustrative, not a pin-map — no point is a real address; low-count areas stay dark."
    />
  )
}
